<template>
  <div class="news-page">
    <!-- 左側篩選側邊欄 -->
    <div class="filter-panel">
      <div class="filter-card">
        <h2 class="filter-title">篩選公告</h2>

        <!-- 基本篩選 -->
        <div class="filter-section">
          <h3 class="filter-label">狀態</h3>
          <div class="filter-group">
            <button
              @click="setFilter('all')"
              class="filter-btn"
              :class="{ active: currentFilter === 'all' }"
            >
              <span>全部</span>
              <span class="filter-count">{{ newsList.length }}</span>
            </button>
            <button
              @click="setFilter('unread')"
              class="filter-btn"
              :class="{ active: currentFilter === 'unread' }"
            >
              <span>未讀</span>
              <span class="filter-count">{{ unreadCount }}</span>
            </button>
            <button
              @click="setFilter('read')"
              class="filter-btn"
              :class="{ active: currentFilter === 'read' }"
            >
              <span>已讀</span>
              <span class="filter-count">{{ readCount }}</span>
            </button>
          </div>
        </div>

        <!-- 類別篩選 -->
        <div v-if="newsTypes.length > 0" class="filter-section">
          <h3 class="filter-label">類別</h3>
          <div class="filter-group">
            <button
              v-for="type in newsTypes"
              :key="type.id"
              @click="setFilter('type', type.id)"
              class="filter-btn"
              :class="{ active: currentFilter === 'type' && currentTypeId === type.id }"
            >
              <span>{{ type.name }}</span>
              <span class="filter-count">{{ type.count }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 右側公告列表 -->
    <div class="news-list">
      <h1 class="news-page-title">最新公告</h1>

      <div v-if="loading" class="news-loading">
        <div class="news-spinner"></div>
        <p>載入中...</p>
      </div>

      <div v-else-if="filteredNewsList.length === 0" class="news-empty">
        <i class="fas fa-inbox"></i>
        <p>目前沒有符合條件的公告</p>
      </div>

      <div v-else>
        <div
          v-for="news in filteredNewsList"
          :key="news.news_id"
          @click="openNewsDetail(news)"
          class="news-card"
          :class="{ unread: !news.is_read }"
        >
          <div class="news-card-header">
            <span class="news-date">
              <i class="fas fa-calendar-alt"></i>
              {{ news.date_added }}
            </span>
          </div>

          <div class="news-title">
            {{ stripHtml(news.news_title) }}
          </div>

          <p class="news-preview">
            {{ stripHtml(news.news_content) }}
          </p>

          <div class="news-card-footer">
            <span class="read-more">閱讀全文 <i class="fas fa-arrow-right"></i></span>
            <span v-if="!news.is_read" class="unread-dot"></span>
          </div>
        </div>
      </div>
    </div>

    <!-- 公告詳情彈窗 -->
    <div
      v-if="showDetailModal"
      @click.self="closeDetail"
      class="modal-overlay"
    >
      <div class="modal-card">
        <div class="modal-header">
          <span class="modal-header-title">{{ stripHtml(selectedNews.news_title) }}</span>
          <button
            @click="closeDetail"
            class="modal-close"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div class="modal-body">
          <div class="modal-date">
            <i class="fas fa-calendar-alt"></i>
            發布日期：{{ selectedNews.date_added }}
          </div>

          <div class="modal-text" v-html="selectedNews.news_content"></div>
        </div>

        <div class="modal-footer">
          <button
            @click="closeDetail"
            class="btn btn-primary"
          >
            關閉
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { newsApi } from '@/api/news.js'

export default {
  name: 'News',
  data() {
    return {
      newsList: [],
      loading: true,
      showDetailModal: false,
      selectedNews: null,
      currentFilter: 'all', // 'all', 'read', 'unread', 'type'
      currentTypeId: null
    }
  },
  computed: {
    // 已讀數量
    readCount() {
      return this.newsList.filter(news => news.is_read).length
    },
    // 未讀數量
    unreadCount() {
      return this.newsList.filter(news => !news.is_read).length
    },
    // 公告類別列表（動態）
    newsTypes() {
      const typesMap = new Map()
      this.newsList.forEach(news => {
        if (news.news_type && news.news_type_name) {
          if (!typesMap.has(news.news_type)) {
            typesMap.set(news.news_type, {
              id: news.news_type,
              name: news.news_type_name,
              count: 0
            })
          }
          typesMap.get(news.news_type).count++
        }
      })
      return Array.from(typesMap.values())
    },
    // 篩選後的公告列表
    filteredNewsList() {
      if (this.currentFilter === 'all') {
        return this.newsList
      } else if (this.currentFilter === 'read') {
        return this.newsList.filter(news => news.is_read)
      } else if (this.currentFilter === 'unread') {
        return this.newsList.filter(news => !news.is_read)
      } else if (this.currentFilter === 'type' && this.currentTypeId) {
        return this.newsList.filter(news => news.news_type === this.currentTypeId)
      }
      return this.newsList
    }
  },
  mounted() {
    this.fetchNewsList()
  },
  methods: {
    setFilter(filterType, typeId = null) {
      this.currentFilter = filterType
      this.currentTypeId = typeId
    },
    fetchNewsList() {
      this.loading = true
      newsApi.getAllNews()
        .then((response) => {
          if (response.data && response.data.success) {
            this.newsList = response.data.data || []
          }
        })
        .catch((error) => {
          console.error('獲取公告列表失敗', error)
        })
        .finally(() => {
          this.loading = false
        })
    },
    openNewsDetail(news) {
      this.selectedNews = news
      this.showDetailModal = true

      // 如果是未讀，標記為已讀
      if (!news.is_read) {
        newsApi.markAsRead(news.news_id)
          .then((response) => {
            if (response.data && response.data.success) {
              // 更新列表中的已讀狀態
              const newsItem = this.newsList.find(n => n.news_id === news.news_id)
              if (newsItem) {
                newsItem.is_read = true
              }
            }
          })
          .catch((error) => {
            console.error('標記為已讀失敗', error)
          })
      }
    },
    closeDetail() {
      this.showDetailModal = false
      this.selectedNews = null
    },
    stripHtml(html) {
      const tmp = document.createElement('DIV')
      tmp.innerHTML = html
      const text = tmp.textContent || tmp.innerText || ''
      return text.length > 150 ? text.substring(0, 150) + '...' : text
    }
  }
}
</script>

<style scoped>
/* ─── NEWS PAGE LAYOUT ─── */
.news-page {
  width: 100%;
  min-height: 100%;
  display: flex;
  gap: 28px;
  padding: 32px 40px;
}

/* ─── FILTER PANEL ─── */
.filter-panel {
  width: 240px;
  flex-shrink: 0;
}

.filter-card {
  background: var(--d2-white, #fff);
  border-radius: 20px;
  box-shadow: 0 4px 24px rgba(15, 43, 74, 0.08);
  padding: 24px;
  position: sticky;
  top: 32px;
}

.filter-title {
  font-family: 'DM Serif Display', serif;
  font-size: 18px;
  color: var(--navy, #0f2b4a);
  margin-bottom: 20px;
}

.filter-section {
  margin-bottom: 20px;
}

.filter-section:last-child {
  margin-bottom: 0;
}

.filter-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 2px;
  color: var(--d2-blue, #2563eb);
  font-weight: 600;
  margin-bottom: 10px;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.filter-btn {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 10px 14px;
  border-radius: 10px;
  font-size: 14px;
  cursor: pointer;
  border: none;
  background: transparent;
  color: var(--charcoal, #1e293b);
  transition: all 0.2s;
  text-align: left;
  font-family: inherit;
}

.filter-btn:hover {
  background: var(--sky-light, #eff6ff);
}

.filter-btn.active {
  background: var(--sky, #dbeafe);
  color: var(--d2-blue, #2563eb);
  font-weight: 600;
}

.filter-count {
  font-size: 12px;
  color: var(--slate, #64748b);
  font-weight: 400;
  background: var(--ice, #f0f5fc);
  padding: 2px 10px;
  border-radius: 12px;
}

.filter-btn.active .filter-count {
  background: rgba(37, 99, 235, 0.12);
  color: var(--d2-blue, #2563eb);
}

/* ─── NEWS LIST ─── */
.news-list {
  flex: 1;
  min-width: 0;
}

.news-page-title {
  font-family: 'DM Serif Display', serif;
  font-size: 36px;
  color: var(--navy, #0f2b4a);
  margin-bottom: 24px;
}

.news-loading {
  text-align: center;
  padding: 48px 0;
  color: var(--slate, #64748b);
}

.news-spinner {
  display: inline-block;
  width: 48px;
  height: 48px;
  border: 3px solid var(--sky, #dbeafe);
  border-top-color: var(--d2-blue, #2563eb);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.news-empty {
  background: var(--d2-white, #fff);
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(15, 43, 74, 0.06);
  padding: 48px;
  text-align: center;
  color: var(--slate, #64748b);
}

.news-empty i {
  font-size: 48px;
  color: var(--d2-blue-light, #60a5fa);
  opacity: 0.4;
  margin-bottom: 16px;
  display: block;
}

.news-empty p {
  font-size: 16px;
}

/* ─── NEWS CARD ─── */
.news-card {
  background: var(--d2-white, #fff);
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(15, 43, 74, 0.06);
  padding: 24px 28px;
  margin-bottom: 12px;
  cursor: pointer;
  transition: all 0.25s ease;
  border-left: 4px solid transparent;
}

.news-card:hover {
  box-shadow: 0 4px 20px rgba(15, 43, 74, 0.12);
  transform: translateX(4px);
}

.news-card.unread {
  border-left-color: var(--d2-blue, #2563eb);
  background: linear-gradient(135deg, rgba(37, 99, 235, 0.03), var(--d2-white, #fff));
}

.news-card-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 10px;
}

.news-date {
  font-size: 12px;
  color: var(--slate, #64748b);
}

.news-date i {
  margin-right: 4px;
}

.news-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--charcoal, #1e293b);
  margin-bottom: 6px;
}

.news-preview {
  font-size: 13px;
  color: var(--slate, #64748b);
  line-height: 1.6;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  margin: 0;
}

.news-card-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 12px;
}

.read-more {
  font-size: 13px;
  color: var(--d2-blue, #2563eb);
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 6px;
}

.read-more:hover {
  color: var(--navy, #0f2b4a);
}

.unread-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--d2-blue, #2563eb);
}

/* ─── MODAL ─── */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 43, 74, 0.55);
  z-index: 5000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  backdrop-filter: blur(4px);
}

.modal-card {
  background: var(--d2-white, #fff);
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(15, 43, 74, 0.25);
  width: 100%;
  max-width: 700px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.modal-header {
  background: var(--d2-blue, #2563eb);
  color: var(--d2-white, #fff);
  padding: 28px 32px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
}

.modal-header-title {
  font-family: 'DM Serif Display', serif;
  font-size: 22px;
  font-weight: 400;
  letter-spacing: 0.5px;
}

.modal-close {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: var(--d2-white, #fff);
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  flex-shrink: 0;
}

.modal-close:hover {
  background: rgba(255, 255, 255, 0.35);
}

.modal-body {
  padding: 32px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}

.modal-date {
  font-size: 13px;
  color: var(--slate, #64748b);
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.modal-date i {
  color: var(--d2-blue-light, #60a5fa);
}

.modal-text {
  font-size: 14px;
  line-height: 1.8;
  color: var(--charcoal, #1e293b);
}

.modal-text :deep(p) {
  margin-bottom: 16px;
}

.modal-text :deep(ul),
.modal-text :deep(ol) {
  margin: 16px 0;
  padding-left: 20px;
}

.modal-text :deep(ul li) {
  margin-bottom: 10px;
  line-height: 1.7;
}

.modal-text :deep(img) {
  max-width: 100%;
  height: auto;
  margin: 1.5rem 0;
  border-radius: 0.5rem;
}

.modal-text :deep(h1),
.modal-text :deep(h2),
.modal-text :deep(h3) {
  font-weight: 600;
  margin-top: 1.5rem;
  margin-bottom: 0.75rem;
  color: var(--charcoal, #1e293b);
}

.modal-footer {
  padding: 20px 32px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  display: flex;
  justify-content: flex-end;
  flex-shrink: 0;
}

.btn {
  padding: 10px 28px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  font-family: inherit;
}

.btn-primary {
  background: var(--d2-blue, #2563eb);
  color: var(--d2-white, #fff);
}

.btn-primary:hover {
  background: var(--navy, #0f2b4a);
}

/* ─── RESPONSIVE ─── */
@media (max-width: 768px) {
  .news-page {
    flex-direction: column;
    padding: 16px;
    gap: 16px;
  }

  .filter-panel {
    width: 100%;
  }

  .filter-card {
    position: static;
  }

  .news-page-title {
    font-size: 26px;
  }

  .modal-card {
    max-width: 95%;
  }

  .modal-overlay {
    padding: 20px;
  }
}

@media (max-width: 480px) {
  .news-page {
    padding: 10px;
  }

  .news-page-title {
    font-size: 22px;
  }

  .news-card {
    padding: 14px;
  }

  .modal-overlay {
    padding: 10px;
  }

  .modal-header {
    padding: 14px 16px;
  }

  .modal-body {
    padding: 16px;
  }

  .modal-footer {
    padding: 12px 16px;
  }
}
</style>
